import { useEffect, useState } from 'react';

/**
 * Returns the current visual-viewport height and the keyboard-offset
 * below the layout viewport (useful for adjusting bottom-sheets so
 * Android's soft-keyboard doesn't cover them).
 *
 * On browsers without `window.visualViewport` (old Safari on iOS < 13,
 * some embedded webviews) this falls back to `window.innerHeight`
 * and zero keyboard offset — the bottom-sheet behaves as before.
 */
export function useVisualViewport() {
  const [state, setState] = useState(() => {
    if (typeof window === 'undefined') return { height: 0, keyboardOffset: 0 };
    const vv = window.visualViewport;
    return {
      height: vv?.height ?? window.innerHeight,
      keyboardOffset: vv ? Math.max(0, window.innerHeight - vv.height) : 0,
    };
  });

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      setState({
        height: vv.height,
        // On Android, when the soft keyboard opens, `vv.height` shrinks
        // but `window.innerHeight` stays the layout viewport. The delta
        // is the keyboard height; consumers push content up by that
        // amount to keep inputs visible.
        keyboardOffset: Math.max(0, window.innerHeight - vv.height),
      });
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  return state;
}
